import { ChannelCardV2, PackageSummary, SvodPromoMap, getAvailabilityText } from "lib/helper/channel-interactive-tool-v2";
import { isChannelsServiceLocal, isLocalChannelExcludedFromSignature, isServiceLocalInPackage, signatureExclusionsForSurface } from "lib/service/channels/get-local-national-dma-eligible-channels";

// Sorted signature package IDs - Internet (used for both ordering and filtering)
const INTERNET_SORTED_PACKAGE_IDS = ["ATT_TV_ENTERTAINMENT", "ATT_TV_CHOICE", "ATT_TV_ULTIMATE", "ATT_TV_PREMIER", "ATT_TV_OPTIMO_MAS"];

// Sorted signature package IDs - Satellite (used for both ordering and filtering)
const SATELLITE_SORTED_PACKAGE_IDS = [
  "DTV_TV_ENTERTAINMENT",
  "DTV_TV_CHOICE",
  "DTV_TV_ULTIMATE",
  "DTV_TV_PREMIER",
  "DTV_MAS_LATINO",
  "DTV_OPTIMO_MAS",
  "DTV_MAS_ULTRA",
  "DTV_LO_MAXIMO"
];

/**
 * Package entry structure returned by all builders
 */
export interface PackageEntry {
  id: string;
  displayLabel: string;
  packageLegal: string;
  ctaObj: any;
  type: string;
  order: number;
}

/**
 * Result structure from package builders
 */
export interface ModalPackageLists {
  signaturePackageList: PackageEntry[];
  genrePackageList: PackageEntry[];
}

/**
 * Options for building modal packages
 */
export interface BuildModalPackagesOptions {
  channel: ChannelCardV2;
  packageSummaries: Record<string, PackageSummary>;
  chnlSrc?: string;
  svodPromoMap?: SvodPromoMap;
  dmaData?: { dma: string; nielsenDma?: string };
  genreLocalChannelMapping?: any[];
  signatureExcludedLocalChannels?: any[];
  isGenrePackagesEnabled?: boolean;
}

/**
 * Normalizes string for package name comparison (canonical form)
 */
const canonical = (s = "") => s
  ?.toLowerCase()
  ?.replace(/™/g, "")
  ?.normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "") // remove accents
  ?.replace(/[^a-z0-9]/g, ""); // remove spaces & punctuation

/**
 * Determines if variant is satellite based on chnlSrc
 */
const isSatelliteVariant = (chnlSrc?: string): boolean => chnlSrc === "dtvClup";

/**
 * Strategy 1: Build packages for regular Channel modal
 * Uses channel.signaturePackages and channel.genrePackages with SVOD promo support
 */
export function buildChannelPackages(options: BuildModalPackagesOptions): ModalPackageLists {
  const { channel, packageSummaries, chnlSrc, svodPromoMap = {}, isGenrePackagesEnabled = true } = options;

  // Check if ALL signature packages have valid order from CMS
  const allPackagesHaveValidOrder = (channel.signaturePackages || []).every(pkgRef => {
    const summary = packageSummaries[pkgRef.id];
    if (!summary) {
      return false;
    }
    const configOrder = summary.order;
    return configOrder != null && configOrder !== 999;
  });

  const signaturePackageList = (channel.signaturePackages || [])
    .map(pkgRef => {
      const summary = packageSummaries[pkgRef.id];
      if (!summary) {
        return null;
      }

      // Check if this channel has SVOD promo text for this package
      const svodPromoText = svodPromoMap[channel.label]?.[pkgRef.id];
      const packageLegal = svodPromoText || getAvailabilityText(pkgRef.availability);

      // Determine sort order based on whether ALL packages have valid CMS order
      let sortOrder: number;

      if (allPackagesHaveValidOrder) {
        sortOrder = typeof summary.order === 'number' ? summary.order : parseInt(String(summary.order), 10) || 999;
      } else {
        // Use chnlSrc to determine correct fallback order (not package type)
        const isSatellite = isSatelliteVariant(chnlSrc);
        const fallbackOrder = isSatellite ? SATELLITE_SORTED_PACKAGE_IDS : INTERNET_SORTED_PACKAGE_IDS;

        const orderIndex = fallbackOrder.indexOf(pkgRef.id);
        sortOrder = orderIndex >= 0 ? orderIndex : 999;
      }

      return {
        id: summary.id,
        displayLabel: summary.displayLabel || pkgRef.id,
        packageLegal,
        ctaObj: summary.ctaObj || null,
        type: summary.type || "internet",
        order: sortOrder
      };
    })
    .filter(Boolean)
    .sort((a, b) => a!.order - b!.order) as PackageEntry[];

  // Skip genre package list building if genre packages are disabled
  const genrePackageList = isGenrePackagesEnabled ?
    (channel.genrePackages || [])
      .map(pkgRef => {
        const summary = packageSummaries[pkgRef.id];
        if (!summary) {
          return null;
        }

        // For genre packages, use genrePackageLegal if available, otherwise check for SVOD promo
        const svodPromoText = svodPromoMap[channel.label]?.[pkgRef.id];
        const packageLegal = summary.genrePackageLegal || svodPromoText || getAvailabilityText(pkgRef.availability);

        return {
          id: summary.id,
          displayLabel: summary.displayLabel || pkgRef.id,
          packageLegal,
          ctaObj: summary.ctaObj || null,
          type: summary.type || "genre",
          order: typeof summary.order === 'number' ? summary.order : parseInt(String(summary.order), 10) || 999
        };
      })
      .filter(Boolean)
      .sort((a, b) => Number(a!.order) - Number(b!.order)) as PackageEntry[] :
    [];

  return {
    signaturePackageList,
    genrePackageList
  };
}

/**
 * Strategy 2: Build packages for RSN modal
 * Uses channel.rawData.packageNames with string matching and product type filtering
 */
export function buildRsnPackages(options: BuildModalPackagesOptions): ModalPackageLists {
  const { channel, packageSummaries, chnlSrc = "" } = options;

  if (!channel?.rawData?.packageNames || !Object.keys(packageSummaries).length) {
    return {
      signaturePackageList: [],
      genrePackageList: []
    };
  }

  // Backend package names list (from API)
  const channelPkgNamesLcRaw = channel.rawData.packageNames || [];
  const channelPkgNamesLc = channelPkgNamesLcRaw.map((p: string) => p?.toLowerCase?.());
  const channelPkgNamesCanonical = channelPkgNamesLcRaw.map((p: string) => canonical(p));

  const isSatellite = isSatelliteVariant(chnlSrc);

  // Filter packages by backend inclusion and product type
  const filtered = Object.values(packageSummaries).filter(pkg => {
    const pkgDisplayName = pkg.displayLabel || pkg.id || "";
    const pkgDisplayNameLc = pkgDisplayName?.toLowerCase?.();
    const pkgCanonical = canonical(pkgDisplayName);

    // Backend inclusion check using canonical forms (exact) OR legacy includes fallback
    const includedByBackend = channelPkgNamesCanonical.includes(pkgCanonical) || channelPkgNamesLc?.some((pn: string) => pn?.includes?.(pkgDisplayNameLc));

    if (!includedByBackend) {
      return false;
    }

    // Filter based on product type using packageConfig.type
    if (isSatellite) {
      // Satellite: include satellite signature packs + genre packs (available on both)
      return pkg.type === "satellite" || pkg.type === "genre" || pkg.type === "genre-mini";
    }
    // Internet/streaming: include internet signature packs + genre packs (available on both)
    return pkg.type === "internet" || pkg.type === "genre" || pkg.type === "genre-mini";
  });

  // Separate into signature and genre packages
  const targetSignatureType = isSatellite ? "satellite" : "internet";

  const signaturePackageList = filtered
    .filter(pkg => pkg.type === targetSignatureType)
    .map(pkg => ({
      id: pkg.id,
      displayLabel: pkg.displayLabel || pkg.id,
      packageLegal: "Included in package",
      ctaObj: pkg.ctaObj || null,
      type: pkg.type || targetSignatureType,
      order: typeof pkg.order === 'number' ? pkg.order : parseInt(String(pkg.order), 10) || 999
    }))
    .sort((a, b) => Number(a.order) - Number(b.order));

  const genrePackageList = filtered
    .filter(pkg => pkg.type === "genre" || pkg.type === "genre-mini")
    .map(pkg => ({
      id: pkg.id,
      displayLabel: pkg.displayLabel || pkg.id,
      packageLegal: pkg.genrePackageLegal || "Included in package",
      ctaObj: pkg.ctaObj || null,
      type: pkg.type || "genre",
      order: typeof pkg.order === 'number' ? pkg.order : parseInt(String(pkg.order), 10) || 999
    }))
    .sort((a, b) => Number(a.order) - Number(b.order));

  return {
    signaturePackageList,
    genrePackageList
  };
}

/**
 * Strategy 3: Build packages for Local channel modal
 * Uses hardcoded signature package IDs and DMA-based genre package filtering
 */
export function buildLocalPackages(options: BuildModalPackagesOptions): ModalPackageLists {
  const { channel, packageSummaries, chnlSrc, dmaData, genreLocalChannelMapping } = options;

  const isSatellite = isSatelliteVariant(chnlSrc);
  const SIGNATURE_PACKAGE_IDS = isSatellite ? SATELLITE_SORTED_PACKAGE_IDS : INTERNET_SORTED_PACKAGE_IDS;
  // [WAT-11768] Stream-only rule — the satellite variant of this modal must not hide a local.
  const signatureExcludedLocalChannels = signatureExclusionsForSurface(options?.signatureExcludedLocalChannels, isSatellite);

  // Check if this local channel is excluded from signature packages (e.g., genre-only channels like Telemundo)
  // Supports per-package exclusion: when excludeFromPackages is set, only those specific packages are removed
  const channelNetwork = channel.rawData?.network || channel.label;

  // Get signature packages (all local channels are included in all signature packages)
  // ...unless excluded via signatureExcludedLocalChannels CMS config (fully or per-package)
  const signaturePackageList = Object.values(packageSummaries)
    .filter(pkg => SIGNATURE_PACKAGE_IDS.includes(pkg.id))
    .filter(pkg => !isLocalChannelExcludedFromSignature(channelNetwork, dmaData?.dma, signatureExcludedLocalChannels, pkg.id, channel.rawData))
    .map(pkg => ({
      id: pkg.id,
      displayLabel: pkg.displayLabel || pkg.id,
      packageLegal: "Included in package",
      ctaObj: pkg.ctaObj || null,
      type: "signature",
      order: typeof pkg.order === 'number' ? pkg.order : parseInt(String(pkg.order), 10) || 999
    }))
    .sort((a, b) => Number(a.order) - Number(b.order));

  // Get genre packages eligible for this local channel based on DMA
  const genrePackageList: PackageEntry[] = [];

  // A channels-service station (WAT-11611) is placed by its own package list, not the CMS mapping.
  const fromService = isChannelsServiceLocal(channel.rawData);
  const channelMapping = !fromService && genreLocalChannelMapping && dmaData?.dma && channel.rawData?.network ?
    genreLocalChannelMapping.find(mapping => mapping.channel === channel.rawData?.network || mapping.channel === channel.label) :
    undefined;

  // Check if channel is eligible for this genre pack in this DMA
  const isInGenre = (pkg: typeof packageSummaries[string]) => {
    if (fromService) {
      return isServiceLocalInPackage(channel.rawData, pkg.id);
    }
    const packageKey = (pkg.queryParam || pkg.id)?.toLowerCase();
    const dmaList = packageKey ? channelMapping?.[packageKey] : undefined;
    if (!dmaList) {
      return false;
    }
    return dmaList.toLowerCase() === "all" || dmaList.split(",").includes(dmaData.dma) || (dmaData.nielsenDma && dmaList.split(",").includes(dmaData.nielsenDma));
  };

  if (fromService || channelMapping) {
    Object.values(packageSummaries)
      .filter(pkg => pkg.type === "genre" && isInGenre(pkg))
      .forEach(pkg => {
        genrePackageList.push({
          id: pkg.id,
          displayLabel: pkg.displayLabel || pkg.id,
          packageLegal: "Included in Base package",
          ctaObj: pkg.ctaObj || null,
          type: "genre",
          order: typeof pkg.order === 'number' ? pkg.order : parseInt(String(pkg.order), 10) || 999
        });
      });

    genrePackageList.sort((a, b) => Number(a.order) - Number(b.order));
  }

  return {
    signaturePackageList,
    genrePackageList
  };
}

/**
 * Main entry point: Routes to appropriate builder based on modal type
 */
export type ModalType = "channel" | "rsn" | "local";

export function buildModalPackageLists(modalType: ModalType, options: BuildModalPackagesOptions): ModalPackageLists {
  let result: ModalPackageLists;

  switch (modalType) {
    case "channel":
      result = buildChannelPackages(options);
      break;
    case "rsn":
      result = buildRsnPackages(options);
      break;
    case "local":
      result = buildLocalPackages(options);
      break;
    default:
      result = {
        signaturePackageList: [],
        genrePackageList: []
      };
  }

  return result;
}
